import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import client from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AnalystTag } from "@/components/AnalystCard";
import { VideoEmbed } from "@/components/VideoEmbed";
import { SubscribeModal } from "@/components/SubscribeModal";
import { UsdtPlans } from "@/components/billing/UsdtPlans";
import { AlphaFeedItem } from "@/components/AlphaFeedItem";
import { ArticleCard } from "@/components/ArticleCard";
import { Lock, PlayCircle, FileText, Radar, Target, BookOpen, Users, CheckCircle2, Instagram } from "lucide-react";

const fmtIDR = (n) => "Rp " + Number(n || 0).toLocaleString("id-ID");

function ModuleItem({ m }) {
  const isVideo = m.module_type === "video";
  return (
    <div data-testid={`module-item-${m.slug}`} className="cl-card overflow-hidden" style={{ transform: "none" }}>
      {m.image && !m.locked && <img src={m.image} alt={m.title} className="w-full h-40 object-cover" />}
      <div className="p-5">
        <div className="flex items-center gap-2 mb-2">
          <span className="font-mono-cl text-[10px] tracking-[0.14em] uppercase text-[#ccff00] flex items-center gap-1">
            {isVideo ? <PlayCircle className="w-3.5 h-3.5" /> : <BookOpen className="w-3.5 h-3.5" />} {isVideo ? "Video" : "Written"} Module
          </span>
          {m.tier === "subscribers" && <span className="font-mono-cl text-[10px] uppercase text-zinc-500">Exclusive</span>}
        </div>
        <h4 className="font-display text-lg font-bold">{m.title}</h4>
        {m.description && <p className="text-sm text-zinc-400 mt-1">{m.description}</p>}
        {m.locked ? (
          <div className="mt-4 rounded-xl border border-[#ccff00]/25 bg-[#ccff00]/[0.04] p-4 text-center">
            <Lock className="w-5 h-5 text-[#ccff00] mx-auto" />
            <p className="text-sm text-zinc-300 mt-2">Subscribe to unlock this module.</p>
          </div>
        ) : (
          <div className="mt-4 space-y-3">
            {isVideo && m.video_url && <VideoEmbed url={m.video_url} />}
            {m.content && <p className="text-sm text-zinc-300 whitespace-pre-wrap leading-relaxed">{m.content}</p>}
            {(m.links || []).length > 0 && (
              <div className="flex flex-wrap gap-2">
                {m.links.map((l, i) => (
                  <a key={i} href={l} target="_blank" rel="noreferrer" className="text-xs text-[#ccff00] underline break-all">{l}</a>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default function AnalystDetail() {
  const { slug } = useParams();
  const { user } = useAuth();
  const [a, setA] = useState(null);
  const [tab, setTab] = useState("modules");
  const [showSub, setShowSub] = useState(false);

  const fetchA = () => client.get(`/analysts/${slug}`).then((r) => setA(r.data)).catch(() => setA(false));
  useEffect(() => { fetchA(); }, [slug]);

  if (a === false) return <div className="py-24 text-center text-zinc-500">Analyst not found.</div>;
  if (!a) return <div className="py-24 text-center text-zinc-500">Loading…</div>;

  const counts = {
    modules: (a.modules || []).length,
    signals: (a.signals || []).length,
    convictions: (a.convictions || []).length,
    articles: (a.articles || []).length,
  };
  const TABS = [
    ["modules", "Modules", BookOpen, counts.modules],
    ["signals", "Signals", Radar, counts.signals],
    ["convictions", "Conviction", Target, counts.convictions],
    ["articles", "Research", FileText, counts.articles],
  ];

  return (
    <div>
      {/* HERO */}
      {/* HEADER: 16:9 landscape banner (shown only on the profile page) */}
      <section className="border-b border-white/10">
        <div className="relative w-full overflow-hidden bg-[#0a0a0a]" style={{ aspectRatio: "16 / 9", maxHeight: "340px" }}>
          {a.big_picture ? (
            <img src={a.big_picture} alt="" className="absolute inset-0 w-full h-full object-cover" style={{ objectPosition: "center" }} />
          ) : (
            <div className="absolute inset-0 bg-gradient-to-br from-zinc-800 to-[#0a0a0a]" />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/40 to-transparent" />
        </div>
        <div className="relative max-w-[1200px] mx-auto px-6">
          <div className="flex flex-col sm:flex-row sm:items-end gap-5 -mt-20 sm:-mt-24 pb-8">
            {/* 3:4 portrait */}
            <div className="w-32 sm:w-40 shrink-0 rounded-2xl overflow-hidden border-2 border-[#ccff00]/40 shadow-2xl bg-black" style={{ aspectRatio: "3 / 4" }}>
              {a.profile_image
                ? <img src={a.profile_image} alt={a.name} className="w-full h-full object-cover" style={{ objectPosition: "center 22%" }} />
                : <div className="w-full h-full grid place-items-center bg-gradient-to-br from-zinc-700 to-zinc-950"><span className="font-display text-5xl font-black text-white/25">{(a.name || "?")[0]}</span></div>}
            </div>
            <div className="flex-1 min-w-0 pb-1">
              <div className="flex flex-wrap gap-1.5 mb-3">{(a.types || []).map((t) => <AnalystTag key={t} label={t} />)}</div>
              <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tight">{a.name}</h1>
              <p className="text-zinc-300 mt-2 max-w-2xl">{a.tagline}</p>
              <div className="flex items-center gap-4 mt-3 text-[11px] font-mono-cl tracking-[0.12em] uppercase text-zinc-500">
                <span className="flex items-center gap-1.5"><Users className="w-3.5 h-3.5" /> {a.subscribers || 0} subscribers</span>
              </div>
            </div>
            <div className="shrink-0 pb-1">
              {a.subscribed ? (
                <span data-testid="analyst-subscribed" className="inline-flex items-center gap-2 px-5 py-3 rounded-full border border-[#ccff00]/40 text-[#ccff00] font-semibold text-sm">
                  <CheckCircle2 className="w-4 h-4" /> Subscribed
                </span>
              ) : a.whitelist_status === "pending" ? (
                <span data-testid="analyst-wl-pending" className="inline-flex items-center gap-2 px-5 py-3 rounded-full border border-amber-500/40 text-amber-300 font-semibold text-sm">
                  Whitelist pending
                </span>
              ) : (
                <button data-testid="analyst-subscribe-btn" onClick={() => setShowSub(true)}
                  className="px-6 py-3 rounded-full bg-[#ccff00] text-black font-semibold text-sm hover:brightness-110 transition-[filter]">
                  Get Access
                </button>
              )}
              {!a.subscribed && <UsdtPlans analystId={a.id} />}
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-[1200px] mx-auto px-6 py-10 grid lg:grid-cols-[1.6fr_1fr] gap-10">
        <div>
          {/* tabs */}
          <div className="flex flex-wrap gap-2 mb-6 border-b border-white/10 pb-4">
            {TABS.map(([k, label, Icon, n]) => (
              <button key={k} data-testid={`analyst-tab-${k}`} onClick={() => setTab(k)}
                className={`px-4 py-2 rounded-full text-sm font-medium flex items-center gap-2 transition-colors ${tab === k ? "bg-[#ccff00] text-black" : "border border-white/10 text-zinc-400 hover:text-white"}`}>
                <Icon className="w-4 h-4" /> {label} <span className="opacity-60">{n}</span>
              </button>
            ))}
          </div>

          {tab === "modules" && (
            <div className="grid sm:grid-cols-2 gap-5">
              {(a.modules || []).map((m) => <ModuleItem key={m.slug} m={m} />)}
              {counts.modules === 0 && <p className="text-zinc-500 py-8">No modules published yet.</p>}
            </div>
          )}
          {tab === "signals" && (
            <div className="cl-card overflow-hidden" style={{ transform: "none" }}>
              {(a.signals || []).map((s) => <AlphaFeedItem key={s.id} item={s} />)}
              {counts.signals === 0 && <p className="text-zinc-500 p-8">No signals yet.</p>}
            </div>
          )}
          {tab === "convictions" && (
            <div className="grid sm:grid-cols-2 gap-5">
              {(a.convictions || []).map((c) => (
                <Link key={c.slug} to={`/conviction/${c.slug}`} className="cl-card p-5">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-display text-lg font-bold">{c.asset}</span>
                    <span className="font-mono-cl text-[10px] uppercase text-[#ccff00]">{c.status}</span>
                  </div>
                  <p className="text-sm text-zinc-400 line-clamp-2">{c.title}</p>
                  {c.locked && c.blur_preview && (
                    <span className="inline-flex items-center gap-1.5 mt-3 text-[11px] font-mono-cl tracking-[0.14em] uppercase text-[#ccff00]">
                      <Lock className="w-3 h-3" /> Exclusive · Locked
                    </span>
                  )}
                </Link>
              ))}
              {counts.convictions === 0 && <p className="text-zinc-500 py-8">No conviction theses yet.</p>}
            </div>
          )}
          {tab === "articles" && (
            <div className="grid sm:grid-cols-2 gap-5">
              {(a.articles || []).map((ar) => <ArticleCard key={ar.slug} a={ar} />)}
              {counts.articles === 0 && <p className="text-zinc-500 py-8">No research yet.</p>}
            </div>
          )}
        </div>

        {/* SIDEBAR: intro video + bio */}
        <aside className="space-y-6">
          {a.intro_video_url && (
            <div>
              <p className="font-mono-cl text-[11px] tracking-[0.2em] uppercase text-[#ccff00] mb-2">Introduction</p>
              <VideoEmbed url={a.intro_video_url} />
            </div>
          )}
          {a.bio && (
            <div className="cl-card p-5" style={{ transform: "none" }}>
              <p className="font-mono-cl text-[11px] tracking-[0.2em] uppercase text-zinc-400 mb-2">Biography</p>
              <p className="text-sm text-zinc-300 whitespace-pre-wrap leading-relaxed">{a.bio}</p>
            </div>
          )}
          {a.socials && (a.socials.instagram || a.socials.twitter) && (
            <div className="flex gap-2">
              {a.socials.instagram && <a href={a.socials.instagram} target="_blank" rel="noreferrer" className="grid place-items-center w-9 h-9 rounded-full border border-white/10 text-zinc-300 hover:text-[#ccff00]"><Instagram className="w-4 h-4" /></a>}
            </div>
          )}
        </aside>
      </div>

      {showSub && <SubscribeModal analyst={a} onClose={() => setShowSub(false)} onSubscribed={fetchA} />}
    </div>
  );
}
